import * as dbDocumentationsService from '../db/documentations/documentationsService';
import * as dbDirectories from 'serviceWorker/db/directories/directories';
import { DomainMessagesTypes } from 'shared/domain/messages/message';
import { awaitEntities } from 'serviceWorker/services/factories/awaitEntities';
import { waitForMessage } from 'serviceWorker/services/factories/waitForMessage';
import { DocumentationModel } from 'shared/domain/documentation/documentationModel';
import {
  DocumentationFilterCellValue,
  DocumentationFilterModel,
  matchesDocumentationFilter,
} from 'shared/domain/documentation/filtering/matchesDocumentationFilter';
import { toJSDate } from 'shared/utils/date';
import { DirectoryModel } from 'shared/domain/directory/directoryModel';
import { DirectoryEntity } from 'serviceWorker/repository/directory/entity';
import {
  getAllDocumentationModels,
  getDirectoriesByQuery,
  getDocumentationsByQuery,
} from './documentationsData';
import { documentationFilteringCacheManager } from 'serviceWorker/repository/documentation/documentationFilteringCacheManager/index';
import { documentationContract } from 'shared/domain/documentation/documentationContract';

interface FilteredDirectoryRequest {
  parentId: string;
  filterModel: DocumentationFilterModel;
  documentState: 'published' | 'archived';
  timezone: string;
  organizationId: string | null;
}

interface FilteredDirectoryResponse {
  directories: DirectoryModel[];
  documentations: DocumentationModel[];
}

function isFilteredDirectoryRequest(value: unknown): value is FilteredDirectoryRequest {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const payload = value as Record<string, unknown>;
  const hasValidState =
    payload.documentState === 'published' || payload.documentState === 'archived';
  const hasValidFilterModel =
    typeof payload.filterModel === 'object' && payload.filterModel !== null;
  const hasValidOrganization =
    typeof payload.organizationId === 'string' || payload.organizationId === null;

  return (
    typeof payload.parentId === 'string' &&
    hasValidFilterModel &&
    hasValidState &&
    typeof payload.timezone === 'string' &&
    hasValidOrganization
  );
}

function createServiceWorkerValueGetters(
  _organizationId: string | null
): Record<string, (field: string, row: DocumentationModel) => DocumentationFilterCellValue> {
  return {
    modifiedAt: (_field: string, row: DocumentationModel): Date | null => toJSDate(row.modifiedAt),
    createdAt: (_field: string, row: DocumentationModel): Date | null => toJSDate(row.createdAt),
    createdBy: (_field: string, row: DocumentationModel) => {
      const createdBy = row.createdBy;
      if (!createdBy?._id) {
        return [];
      }

      return [
        {
          _id: createdBy._id,
        },
      ];
    },
    modifiedBy: (_field: string, row: DocumentationModel) => {
      const modifiedBy = row.modifiedBy;
      if (!modifiedBy?._id) {
        return [];
      }

      return [
        {
          _id: modifiedBy._id,
        },
      ];
    },
    sites: (_field: string, row: DocumentationModel) => row.sites || [],
    levels: (_field: string, row: DocumentationModel) => row.levels || [],
    state: (_field: string, row: DocumentationModel) =>
      row.deleted ? 'archived' : 'published',
  };
}

function getFilterCellValue(
  field: string,
  row: DocumentationModel | DirectoryModel,
  valueGetters: Record<string, (field: string, row: DocumentationModel) => DocumentationFilterCellValue>
): DocumentationFilterCellValue {
  const getter = valueGetters[field];
  if (getter && 'versions' in row) {
    return getter(field, row as DocumentationModel);
  }

  if (field === 'state') {
    return row.deleted ? 'archived' : 'published';
  }

  const rawRow = row as unknown as { [key: string]: unknown };
  return rawRow[field] as DocumentationFilterCellValue;
}

function buildDirectoryParentMap(
  allDirectories: DirectoryEntity[]
): Map<string, string> {
  const parentMap = new Map<string, string>();

  allDirectories.forEach((directory) => {
    if (directory._id) {
      parentMap.set(directory._id, directory.parentId || '');
    }
  });

  return parentMap;
}

function getDirectoriesMatchingDocumentations(
  directories: DirectoryModel[],
  matchingDocumentations: DocumentationModel[],
  directoryParentMap: Map<string, string>
): Set<string> {
  const matchingDirectoryIds = new Set<string>();

  for (const documentation of matchingDocumentations) {
    let currentDirectoryId = documentation.parentId || '';

    while (currentDirectoryId) {
      if (matchingDirectoryIds.has(currentDirectoryId)) {
        break;
      }
      matchingDirectoryIds.add(currentDirectoryId);

      const parentId = directoryParentMap.get(currentDirectoryId);
      if (!parentId || parentId === currentDirectoryId) {
        break;
      }
      currentDirectoryId = parentId;
    }
  }

  directories.forEach((directory) => {
    if (!directory._id) {
      return;
    }

    if (!matchingDirectoryIds.has(directory._id)) {
      matchingDirectoryIds.delete(directory._id);
    }
  });

  return matchingDirectoryIds;
}

/** Directories and files of `parentId` that match the filter model. */
export async function getFilteredDirectory(
  payload: unknown
): Promise<FilteredDirectoryResponse> {
  if (!isFilteredDirectoryRequest(payload)) {
    throw new Error('Invalid filtered directory payload');
  }

  await awaitEntities(
    dbDocumentationsService,
    waitForMessage(
      documentationContract,
      DomainMessagesTypes.allDocumentations
    )
  );

  const {
    parentId,
    filterModel,
    documentState,
    timezone,
    organizationId,
  } = payload as FilteredDirectoryRequest;

  const cacheKeyInput = {
    parentId,
    filterModel,
    documentState,
    timezone,
    organizationId,
  };

  const cachedResponse =
    documentationFilteringCacheManager.getFilteredDirectoryResponseFromCache(
      cacheKeyInput
    );
  if (cachedResponse) {
    return cachedResponse;
  }

  const snapshots =
    documentationFilteringCacheManager.getDocumentationFilteringSnapshots();

  const [baseDirectories, baseDocumentations, allDocumentations, allDirectories] =
    await Promise.all([
      getDirectoriesByQuery(parentId),
      getDocumentationsByQuery(parentId),
      snapshots
        ? Promise.resolve(snapshots.allDocumentations)
        : getAllDocumentationModels(),
      snapshots
        ? Promise.resolve(snapshots.allDirectories)
        : dbDirectories.get(),
    ]);

  if (!snapshots) {
    documentationFilteringCacheManager.setDocumentationFilteringSnapshots(
      allDocumentations,
      allDirectories
    );
  }

  const directoryParentMap = buildDirectoryParentMap(allDirectories);
  const valueGetters = createServiceWorkerValueGetters(organizationId);
  const stateFilterOptions = {
    fixedFilterField: 'state',
    fixedFilterValue: documentState,
  } as const;

  const filteredDocumentations = baseDocumentations.filter((documentation) =>
    matchesDocumentationFilter(
      documentation,
      filterModel,
      (field, row) => getFilterCellValue(field, row, valueGetters),
      timezone,
      stateFilterOptions
    )
  );

  const selfMatchedDirectoryIds = new Set(
    baseDirectories
      .filter((directory) =>
        matchesDocumentationFilter(
          directory,
          filterModel,
          (field, row) => getFilterCellValue(field, row, valueGetters),
          timezone,
          stateFilterOptions
        )
      )
      .map((directory) => directory._id)
      .filter((directoryId): directoryId is string => Boolean(directoryId))
  );

  const matchingDocumentations = allDocumentations.filter((documentation) =>
    matchesDocumentationFilter(
      documentation,
      filterModel,
      (field, row) => getFilterCellValue(field, row, valueGetters),
      timezone,
      stateFilterOptions
    )
  );

  const matchingDirectoryIds = getDirectoriesMatchingDocumentations(
    baseDirectories,
    matchingDocumentations,
    directoryParentMap
  );

  const filteredDirectories = baseDirectories
    .filter((directory) => {
      const directoryId = directory._id || '';
      return (
        selfMatchedDirectoryIds.has(directoryId) ||
        matchingDirectoryIds.has(directoryId)
      );
    })
    .map((directory) => {
      const directoryId = directory._id || '';
      return {
        ...directory,
        containsMatchingFiles: matchingDirectoryIds.has(directoryId),
        selfMatch: selfMatchedDirectoryIds.has(directoryId),
      };
    });

  const nonEmptyDirectoryIds = new Set<string>();
  for (const directory of allDirectories) {
    if (directory.parentId) {
      nonEmptyDirectoryIds.add(directory.parentId);
    }
  }
  for (const documentation of allDocumentations) {
    if (documentation.parentId) {
      nonEmptyDirectoryIds.add(documentation.parentId);
    }
  }

  const directoriesWithEmptyMark = filteredDirectories.map((directory) => ({
    ...directory,
    isEmptyDirectory: !nonEmptyDirectoryIds.has(directory._id || ''),
  }));

  const response: FilteredDirectoryResponse = {
    directories: directoriesWithEmptyMark,
    documentations: filteredDocumentations,
  };

  documentationFilteringCacheManager.setFilteredDirectoryResponseCache(
    cacheKeyInput,
    response
  );

  return response;
}
